import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Nullable } from 'primeng/ts-helpers';
import {
    BehaviorSubject,
    interval,
    Observable,
    Subscription,
    tap,
    throwError
} from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiResponse,
    ApiService
} from '@/api';
import {
    AuthProvider,
    LoginScope
} from '@/auth/enums';
import { OAuth2Init } from '@/auth/models';
import { CustomHttpParamEncoder } from '@/core/http';
import { User } from '@/interfaces';

export enum TokenType {
    ACCESS = 'access_token',
    REFRESH = 'refresh_token',
}

@Injectable({
    providedIn: 'root'
})
class AuthService {
    private _user: BehaviorSubject<Nullable<User>> = new BehaviorSubject<Nullable<User>>(null);
    private _sessionTimer: Nullable<Subscription> = null;
    private _sessionLt = 0;
    private _sessionLeft$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    constructor(
        private apiService: ApiService,
        private router: Router
    ) {
        const userData = localStorage.getItem('user');
        if (userData) {
            this._user.next(JSON.parse(userData));
        }

        this._sessionLt = Number(localStorage.getItem('session_lt') ?? 0);
    }

    set user(user: Nullable<User>) {
        this._user.next(user);
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        } else {
            localStorage.removeItem('user');
            localStorage.removeItem(TokenType.ACCESS);
            localStorage.removeItem(TokenType.REFRESH);
        }
    }

    get user(): Nullable<User> {
        if (!this._user.getValue()) {
            const userData = localStorage.getItem('user');
            if (userData) {
                this._user.next(JSON.parse(userData));
            }
        }
        return this._user.getValue();
    }

    get user$(): Observable<Nullable<User>> {
        return this._user.asObservable();
    }

    setToken(token: string, type: TokenType = TokenType.ACCESS): void {
        localStorage.setItem(type, token);
    }

    getToken(type: TokenType = TokenType.ACCESS): Nullable<string> {
        return localStorage.getItem(type);
    }

    set sessionLt(lt: number) {
        this._sessionLt = lt - 2; // 2s запас
        localStorage.setItem('session_lt', this._sessionLt.toString());
    }

    get sessionLt(): number {
        return this._sessionLt;
    }

    get sessionLeft$(): Observable<number> {
        return this._sessionLeft$.asObservable();
    }

    startSessionTimer(): void {
        if (this._sessionTimer) {
            this._sessionTimer.unsubscribe();
        }

        let elapsed = 0;
        this._sessionTimer = interval(1000).subscribe(() => {
            elapsed++;
            const left = this._sessionLt - elapsed;
            this._sessionLeft$.next(left);
            if (left <= 0) {
                this.logout('session-expired');
            }
        });
    }

    stopSessionTimer(): void {
        if (this._sessionTimer) {
            this._sessionTimer.unsubscribe();
        }
    }

    me(): Observable<User> {
        return this.apiService.get<User>('/auth/me').pipe(
            map((res: ApiResponse<User>) => res.data),
            tap((user: User) => (this.user = user))
        );
    }

    login(data: Record<string, any>): Observable<any> {
        return this.apiService.post<User>('/auth/login', data).pipe(
            map((res: ApiResponse<any>) => res.data),
            tap((payload) => {
                this.user = payload.user;
                this.setToken(payload.access_token, TokenType.ACCESS);
                if (payload.refresh_token) {
                    this.setToken(payload.refresh_token, TokenType.REFRESH);
                }
                this.sessionLt = payload.expires_in ?? 3600;
                this.startSessionTimer();
                this.router.navigate(['/']).then();
            })
        );
    }

    refreshToken(): Observable<string> {
        const refresh = this.getToken(TokenType.REFRESH);
        if (!refresh) {
            return throwError(() => new Error('Missing refresh token'));
        }

        return this.apiService.post<{
            access_token: string;
            refresh_token: string;
            expires_in: number;
        }>('/auth/token/refresh', { refresh_token: refresh })
            .pipe(
                tap(res => {
                    this.setToken(res['access_token'], TokenType.ACCESS);
                    this.setToken(res['refresh_token'], TokenType.REFRESH);
                    this.sessionLt = res['expires_in'] ?? 3600;
                    this.startSessionTimer();
                }),
                map(res => res['access_token'])
            );
    }

    logout(reason?: string): void {
        if (this.user) {
            this.apiService.post<null>('/auth/logout').subscribe();
        }
        this.stopSessionTimer();
        this.user = null;

        const queryParams = reason ? { reason } : {};
        this.router.navigate(['/login'], { queryParams }).then();
    }

    oAuth2Init(provider: AuthProvider, scope: string = LoginScope.WEB_APP): Observable<OAuth2Init> {
        localStorage.setItem('oauth2-provider', provider);
        localStorage.setItem('auth-scope', scope);
        return this.apiService.get<OAuth2Init>(`/auth/${provider}`, { scope })
            .pipe(map((response) => response.data));
    }

    oAuth2Callback(params: Record<string, string>): Observable<User> {
        const provider: string = localStorage.getItem('oauth2-provider') ?? AuthProvider.AZURE;
        if (!provider) {
            throw new Error('OAuth2 provider is missing.');
        }

        params['scope'] = params['scope'] ?? localStorage.getItem('auth-scope') ?? LoginScope.WEB_APP;

        let httpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });
        Object.keys(params).forEach(key => {
            httpParams = httpParams.append(key, params[key]);
        });

        return this.apiService.get<User>(`/auth/${provider}`, httpParams)
            .pipe(
                map((response) => response.data),
                tap((payload: any) => {
                    this.user = payload.user ?? payload;
                    if (payload.access_token) {
                        this.setToken(payload.access_token, TokenType.ACCESS);
                    }
                    if (payload.refresh_token) {
                        this.setToken(payload.refresh_token, TokenType.REFRESH);
                    }
                    this.sessionLt = payload.expires_in ?? 3600;
                    this.startSessionTimer();
                })
            );
    }

    forgotPassword(email: string): Observable<void> {
        return this.apiService.post('/auth/forgot-password', { email }).pipe(
            map((res: ApiResponse<any>) => res.data)
        );
    }

    resetPassword(data: Record<string, any>): Observable<any> {
        return this.apiService.post('/auth/reset-password', data);
    }

    profile(data: Record<string, any>): Observable<any> {
        return this.apiService.patch<User>('/auth/profile', data)
            .pipe(map(response => response.data));
    }

    hasPermission(key: string): boolean {
        return this.user?.permissions?.includes(key) ?? false;
    }
}

export default AuthService
